import { CUSTOMERS_WITH_CUSTOM_DETAILS_VIEW_BOTTOM_ITEMS } from "../../../constants/customers";
import { DataDumpAggregateType } from "../../../types/types";
import {
  useHubletViewDataDump,
  useVisibleGroups,
  useDetailKey,
  useSummaryGroup,
  useHubletViewRollingWindowDataDump,
  useFocusRange,
  useSegmentsForProcess,
} from "utils/customHooks/genericHooks";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useLastDataDumpRow } from "components/DataGetter/LastDataDumpRowGetter";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { useDispatch } from "react-redux";
import { updateHubletViewCurrentRatePerMs } from "providers/GlobalStateProvider";
import { useInterval, useUnmount } from "react-use";
import { useSelector } from "store/useSelector";
import { getRollup } from "./hubletViewUtils";
import { useTransition } from "react";

/** track current rate and dispatch to store.legacy.hubletViewCurrentRatePerMs (used in forecasted lot completion module & useBottomRightItem) */

export default function CurrentRateTracker() {
  useTrackCurrentRate();
  return null;
}
const TRACK_CURRENT_RATE_INTERVAL = 1 * 1000;
/** track current rate and dispatch to store.legacy.hubletViewCurrentRatePerMs (used in forecasted lot completion module & useBottomRightItem) */
function useTrackCurrentRate() {
  const now = Date.now();
  const dataDumpAggregateRollingWindow = useHubletViewRollingWindowDataDump();
  const ckey = useCustomerKey();
  const focusRange = useFocusRange();
  const dataDumpAggregateThisShiftThisSku = useHubletViewDataDump();
  const visibleGroups = useVisibleGroups();
  const summaryGroup = useSummaryGroup();
  const isStorybook = useIsStorybook();
  const currentGroup = visibleGroups.find(
    group => group.summaryGroup === summaryGroup
  );
  const [lastDataDumpRow] = useLastDataDumpRow();

  const detailKey = useDetailKey();
  const process = currentGroup?.processes?.[isStorybook ? 0 : detailKey];
  const processId = process?.id;
  const dataDumpAggregateThisShift: DataDumpAggregateType | null = useSelector(
    state =>
      (processId
        ? state.legacy.dataDumpsFullRangeByProcess[processId]
        : null) ?? null
  );
  const processSegments = useSegmentsForProcess(processId ?? null);

  const [
    {
      hubletView: {
        useRunRateInsteadOfCurrentRate: shouldUseRunRate,
        showCountsSinceShiftStartOnly,
      },
    },
  ] = useFlightConfig();

  const [flightConfig] = useFlightConfig();
  const hubletViewBottomRowItemsConfig =
    flightConfig?.hubletView.bottomRowItems ??
    CUSTOMERS_WITH_CUSTOM_DETAILS_VIEW_BOTTOM_ITEMS[ckey];
  // if counts use a specified dataDumpName (item1), we'll use same for rate (item3)
  const customItem1 = processId
    ? hubletViewBottomRowItemsConfig?.item1?.[processId] ||
      hubletViewBottomRowItemsConfig?.item1?.ALL_PROCESSES
    : null;
  // allow custom numerator in rate calculation e.g. ticksPort3 instead of default item1.dataDumpName or netOutput (this one is only available via flightConfig)
  const customItem3Numerator = (flightConfig?.hubletView.bottomRowItems?.item3
    ?.numeratorDataDumpName ?? null) as string | null;

  const countsDataDumpKey =
    customItem3Numerator ?? customItem1?.dataDumpName ?? "netOutput";

  let count, productionTime;
  const { isUsingRateRollingTimeWindow } = useSettings();
  if (isUsingRateRollingTimeWindow) {
    count = dataDumpAggregateRollingWindow?.[countsDataDumpKey] ?? null;
    productionTime = shouldUseRunRate
      ? getProducingTimeForRunRate(
          dataDumpAggregateRollingWindow,
          flightConfig?.hubletView
            .shouldExcludeSlowdownsAndMinorStopsInRateDenominator
        )
      : (dataDumpAggregateRollingWindow?.productionTime ?? null);
  } else {
    const dataDumpToUse = shouldUseRunRate
      ? dataDumpAggregateThisShift
      : dataDumpAggregateThisShiftThisSku;
    // Use the end of the time range if it is BEFORE now. Otherwise our calculations use an incorrect time range.
    const rangeLtRaw = dataDumpToUse?.range?.lt;
    const endTime = rangeLtRaw && rangeLtRaw < now ? rangeLtRaw : now;
    const productionTimeSoFarThisShift = dataDumpToUse?.productionTime
      ? getProducingTimeForRunRate(
          dataDumpToUse,
          flightConfig?.hubletView
            .shouldExcludeSlowdownsAndMinorStopsInRateDenominator
        )
      : focusRange &&
        dataDumpToUse &&
        endTime - focusRange.ge - (dataDumpToUse?.notScheduledTime || 0);
    const shiftTimeForRate = shouldUseRunRate
      ? getProducingTimeForRunRate(
          dataDumpAggregateThisShift,
          flightConfig?.hubletView
            .shouldExcludeSlowdownsAndMinorStopsInRateDenominator
        )
      : productionTimeSoFarThisShift;
    const skuStartMs = lastDataDumpRow?.skuStart;
    const shiftStart = focusRange?.ge;
    const rollupSinceShiftOrSkuStart =
      processSegments && shiftStart
        ? getRollup(
            processSegments,
            Math.max(skuStartMs || 0, shiftStart),
            endTime
          )
        : null;

    const NPSTime = dataDumpAggregateRollingWindow
      ? dataDumpAggregateRollingWindow?.notScheduledTime
      : rollupSinceShiftOrSkuStart?.notScheduled;
    const shiftOrSkuStartTime = skuStartMs
      ? Math.max(shiftStart || 0, skuStartMs)
      : 0;

    const shiftOrSkuOrRollingWindowStartTime = dataDumpAggregateRollingWindow
      ? dataDumpAggregateRollingWindow.range.ge
      : skuStartMs
        ? shiftOrSkuStartTime
        : 0;
    const skuTimeSoFarWithoutNPS = skuStartMs
      ? endTime - shiftOrSkuOrRollingWindowStartTime - (NPSTime || 0)
      : 0; // don't count NPS time in calculating the target

    count = dataDumpToUse?.[countsDataDumpKey] ?? null;
    const skuTimeSoFarMS = Math.max(
      // minimum 1 second to avoid flash of huge numbers (target count = target rate / sku time so far without NPS time)
      1000,
      skuTimeSoFarWithoutNPS || 0
    );

    productionTime =
      (shouldUseRunRate
        ? productionTimeSoFarThisShift
        : showCountsSinceShiftStartOnly
          ? shiftTimeForRate
          : skuTimeSoFarMS) ?? null;
  }
  const isViewingPastData = focusRange && focusRange?.lt < now;
  const currentRatePerMs =
    productionTime === 0
      ? 0
      : (count || count === 0) &&
          productionTime &&
          // don't show until at least 5 seconds of production time
          (isViewingPastData ||
            productionTime > MIN_PRODUCTION_TIME_MS_BEFORE_SHOW_RATE)
        ? count / productionTime
        : null;

  const dispatch = useDispatch();

  const [, startTransition] = useTransition();
  useInterval(() => {
    if (currentRatePerMs || currentRatePerMs === 0) {
      startTransition(() => {
        dispatch(updateHubletViewCurrentRatePerMs(currentRatePerMs));
      });
    }
  }, TRACK_CURRENT_RATE_INTERVAL);

  useUnmount(() => {
    startTransition(() => {
      dispatch(updateHubletViewCurrentRatePerMs(null));
    });
  });
}

const MIN_PRODUCTION_TIME_MS_BEFORE_SHOW_RATE = 5 * 1000;

function getProducingTimeForRunRate(
  dataDumpAggregateThisShift: DataDumpAggregateType | null,
  shouldExcludeSlowdownsAndMinorStops: boolean
) {
  return (
    (dataDumpAggregateThisShift?.producingTime ?? 0) +
    (shouldExcludeSlowdownsAndMinorStops
      ? 0
      : (dataDumpAggregateThisShift?.minorStopTime ?? 0) +
        (dataDumpAggregateThisShift?.slowdownLossTime ?? 0))
  );
}
